import { gql } from "@apollo/client";

/**
 * Deliberately slim documents for the BYOC dashboard.
 *
 * `GET_TRANSACTIONS` selects roughly forty fields per node including nested
 * `user`, `card`, `merchantData`, `policyStatus`, `expenseCategory` and
 * `allocationCode`, each of which the server hydrates per row. The spend charts
 * need a few scalars. Since `transactions` nulls its limit whenever a date filter
 * is set and returns the entire window in one response, the size of the
 * selection set is what decides whether this is affordable.
 */

/**
 * Every transaction in the window, flat, for client-side bucketing.
 *
 * Ninety days covers all three things the charts need: the trailing 30-day area
 * series, the prior 30 days it is compared against, and eight weekly buckets for
 * the four-vs-four comparison. `DatePeriod` is a closed enum with no
 * "days 31 to 60" member, so the earlier windows are sliced from this payload
 * rather than fetched separately.
 *
 * `accountId` is selected so the same payload can produce per-account spend for
 * the Connected accounts panel without a second round trip.
 *
 * `cardType` is declared so the caller can limit the window to external cards.
 * Without it `transactions` returns every card type, and Apollo silently drops
 * a variable the document does not declare.
 */
export const GET_BYOC_SPEND_TRANSACTIONS = gql`
  query byocSpendTransactions(
    $date: DatePeriod
    $type: [TransactionType]
    $transactionStatus: [Status]
    $cardType: [CardType]
  ) {
    transactions(
      date: $date
      type: $type
      transactionStatus: $transactionStatus
      cardType: $cardType
    ) {
      edges {
        node {
          id
          transactionDate
          amount
          currency
          accountId
          type
        }
      }
    }
  }
`;

/**
 * Count of active external cards on one linked account.
 *
 * `first` is omitted on purpose. The `cards` resolver sets `pageInfo.total` to
 * the length of the current page, so a paged request would report the page size
 * rather than the count; with no limit the resolver accumulates everything and
 * the edge count is the real total. `CardConnection.pageInfo` has no `total`
 * field at all, so the count comes from `edges` either way.
 *
 * A separate document from `GET_CARDS` rather than an extra variable on it:
 * that query declares neither `$cardType` nor `$status`, so Apollo silently
 * drops both and `LinkedAccounts` is today counting cards of every type and
 * status. Fixing it there is a real behaviour change and does not belong in
 * the same breath as a dashboard count.
 */
export const GET_ACCOUNT_CARD_COUNT = gql`
  query byocAccountCardCount(
    $accountId: ID
    $cardType: [CardType]
    $cardStatus: [CardStatus]
  ) {
    cards(accountId: $accountId, cardType: $cardType, cardStatus: $cardStatus) {
      edges {
        node {
          id
        }
      }
    }
  }
`;

/**
 * Aggregates for one compliance type across the whole company.
 * See the `complianceMetrics` field in server/graphql/types/Transaction.graphql.
 */
export const GET_COMPLIANCE_METRICS = gql`
  query complianceMetrics($compliance: TransactionCompliance!) {
    complianceMetrics(compliance: $compliance) {
      count
      totalAmount
      oldestTransactionDate
      currency
      byUser {
        userId
        firstName
        lastName
        count
        totalAmount
      }
    }
  }
`;
